import type { ReactNode } from "react";

import { CornerLeaf } from "./CornerLeaf";

/**
 * Consistent section page chrome — a header with number / title /
 * eyebrow, then content. Each section sets its own children. Designed
 * to print cleanly: max-width column, generous breathing room.
 *
 * A single amber leaf hangs from the top-right of every page to give
 * the brand book a quiet decorative through-line.
 */
export function Page({
  number,
  title,
  eyebrow,
  intro,
  children,
}: {
  number: string;
  title: string;
  eyebrow?: string;
  intro?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <CornerLeaf position="right" top={0} size={70} />
      <div className="mx-auto max-w-[920px] px-7 py-16 md:px-14 md:py-24">
        <div className="flex items-baseline gap-4">
          <span className="font-display text-[20px] tabular-nums text-amber">
            {number}
          </span>
          <span className="h-px flex-1 bg-[color:var(--rule)]" />
          {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        </div>
        <h1
          className="display mt-8 text-ink"
          style={{ fontSize: "clamp(2.2rem, 5.4vw, 4rem)" }}
        >
          {title}
        </h1>
        {intro && (
          <p className="mt-6 max-w-[640px] font-body text-[17px] leading-relaxed text-ink-soft">
            {intro}
          </p>
        )}

        <div className="mt-14">{children}</div>
      </div>
    </div>
  );
}

export function SectionBlock({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-14 first:mt-0">
      <h2 className="eyebrow">{title}</h2>
      <span className="hairline" />
      <div className="mt-6">{children}</div>
    </section>
  );
}
